import { validateCartStock } from './cart-db';
import type { CartItem } from './cart.types';

/**
 * 장바구니 총 가격 계산
 */
export const getTotalPrice = (items: CartItem[]): number => {
  return items.reduce(
    (total, item) => total + item.discountedPrice * item.quantity,
    0
  );
};

/**
 * 포인트 적용 후 최종 가격 계산
 */
export const getFinalPrice = (
  items: CartItem[], 
  usedPoints: number, 
  availablePoints: number = 0
): number => {
  const total = getTotalPrice(items);
  const actualUsedPoints = Math.min(usedPoints, availablePoints, total);
  return total - actualUsedPoints;
};

/**
 * 총 상품 수량 계산
 */
export const getItemCount = (items: CartItem[]): number => {
  return items.reduce((count, item) => count + item.quantity, 0);
};

/**
 * 추가 상품 담기 가능 여부 확인
 */
export const canAddMoreItems = (items: CartItem[]): boolean => {
  return items.length < 5;
};

/**
 * 장바구니 재고 검증
 */
export const validateStock = async (items: CartItem[]) => {
  return await validateCartStock(items);
};

/**
 * 사용 가능한 포인트 범위 계산
 */
export const getValidPointsRange = (
  items: CartItem[], 
  availablePoints: number
): { min: number; max: number } => {
  const totalPrice = getTotalPrice(items);
  return {
    min: 0,
    max: Math.min(availablePoints, totalPrice)
  };
};

/**
 * 포인트 사용량 검증
 */
export const validateUsedPoints = (
  usedPoints: number,
  items: CartItem[],
  availablePoints: number
): boolean => {
  const { min, max } = getValidPointsRange(items, availablePoints);
  return usedPoints >= min && usedPoints <= max;
};

/**
 * 장바구니 상품별 할인 정보 계산
 */
export const getItemDiscountInfo = (item: CartItem) => {
  const discountAmount = item.originalPrice - item.discountedPrice;
  const totalOriginalPrice = item.originalPrice * item.quantity;
  const totalDiscountedPrice = item.discountedPrice * item.quantity;
  const totalDiscountAmount = totalOriginalPrice - totalDiscountedPrice;
  
  return {
    discountAmount,
    totalOriginalPrice,
    totalDiscountedPrice,
    totalDiscountAmount
  };
};

/**
 * 장바구니 전체 할인 정보 계산
 */
export const getCartDiscountInfo = (items: CartItem[]) => {
  const totalOriginalPrice = items.reduce(
    (total, item) => total + item.originalPrice * item.quantity, 
    0
  );
  const totalDiscountedPrice = getTotalPrice(items);
  const totalDiscountAmount = totalOriginalPrice - totalDiscountedPrice;
  const averageDiscountRate = totalOriginalPrice > 0 
    ? (totalDiscountAmount / totalOriginalPrice) * 100 
    : 0;
  
  return {
    totalOriginalPrice,
    totalDiscountedPrice,
    totalDiscountAmount,
    averageDiscountRate
  };
};